import { WIDGET_KEYS } from "@/other/constants/WidgetKeys.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";
import { SWIVU_ASSIST_CONFIG } from "../../../other/constants/OtherKeys.jsx";

import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { cellAvatarTooltipConfig } from "@/other/utils/utils.jsx";
import { EventHelper } from "@bryntum/schedulerpro";
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys.jsx";


const NOTIFY_TYPE_RECORD_KEYS = {
  assignment: "assignedBoardUsers",
  incentive: "eligibleBoardUsers",
  general: "isGeneralUpdates",
  notes: "isNotesUpdates",
  schedule: "isScheduleUpdates",
  checklist: "isChecklistUpdates",
  reminder: "isReminderUpdates",
}

const STORE_BY_NOTIFY_TYPE_KEY_MAP = {
  [NOTIFY_TYPE_RECORD_KEYS.general]: STORE_KEYS.notifyeeBoardUsers,
  [NOTIFY_TYPE_RECORD_KEYS.notes]: STORE_KEYS.notifyeeBoardUsers,
  [NOTIFY_TYPE_RECORD_KEYS.schedule]: STORE_KEYS.notifyeeBoardUsers,
  [NOTIFY_TYPE_RECORD_KEYS.checklist]: STORE_KEYS.notifyeeBoardUsers,
  [NOTIFY_TYPE_RECORD_KEYS.reminder]: STORE_KEYS.notifyeeBoardUsers,
  [NOTIFY_TYPE_RECORD_KEYS.incentive]: STORE_KEYS.resourceStore,
  [NOTIFY_TYPE_RECORD_KEYS.assignment]: STORE_KEYS.resourceStore,
}

const NOTIFY_TYPE_COLUMN_CONTENT = {
  [NOTIFY_TYPE_RECORD_KEYS.general]: {
    icon: "fa-regular fa-envelope w-4 h-4",
    title: "General",
    description: (entityName) => `Notify when this ${entityName}'s general data changes (name, type, etc.)`
  },
  [NOTIFY_TYPE_RECORD_KEYS.notes]: {
    icon: "fa-regular fa-note-sticky w-4 h-4",
    title: "Notes",
    description: (entityName) =>  `Notify when this ${entityName}'s notes are added or updated`
  },
  [NOTIFY_TYPE_RECORD_KEYS.schedule]: {
    icon: "fa-light fa-calendar-days w-4 h-4",
    title: "Schedule",
    description: (entityName) =>  `Notify when this ${entityName} is scheduled or rescheduled`
  },
  [NOTIFY_TYPE_RECORD_KEYS.checklist]: {
    icon: "fa-regular fa-list-check w-4 h-4",
    title: "Checklists",
    description: (entityName) => `Notify when this ${entityName}'s checklists are updated`
  },
  [NOTIFY_TYPE_RECORD_KEYS.reminder]: {
    icon: "fa-regular fa-alarm-clock w-4 h-4",
    title: "Reminders",
    description: (entityName) => `Notify when this ${entityName}'s reminders are triggered`
  },
  [NOTIFY_TYPE_RECORD_KEYS.incentive]: {
    icon: "fa-light fa-award w-4 h-4",
    title: "Incentives",
    description: (entityName) => `View and earn this ${entityName}'s incentives`
  },
  [NOTIFY_TYPE_RECORD_KEYS.assignment]: {
    icon: "fa-regular fa-person w-4 h-4",
    title: "Assignments",
    description: (entityName) => `View this ${entityName}'s assignments on Swivu's Workforce application`
  }
}

const EXCLUDE_NOTIFY_TYPE_BY_TAB = {
  [WIDGET_KEYS.clientNotifyTab]: [NOTIFY_TYPE_RECORD_KEYS.assignment, NOTIFY_TYPE_RECORD_KEYS.incentive],
}

export const HasNotifyBaseMixin = (base) => (
  class extends base {
    static get $name() {
      return "HasNotifyBaseMixin";
    }

    get swivuDeepLinkSlug() {
      return "ownership";
    }

    static get configurable() {
      return {
        title: "Ownership",
        tab : {
          icon: "fa-regular fa-envelope w-4 h-4"
        },
        items: {
          notifyGrid: {
            type: WIDGET_KEYS.swivuModernGrid,
            swivuGridTitle: "Ownership",
            swivuAdditionalRowHeight: 10,
            /*tbar: {
              items: {
                syncChangesAcrossTypesCheckbox: {
                  type: "checkbox",
                  label: "Update Assignees To All",
                  labelCls: "text-swivu-sm !text-black font-medium dark:!text-[#e6e6e6]",
                  listeners: {
                    change: "up.onSyncChangesAcrossTypesChange"
                  }
                }
              }
            },*/
            hideHeaders: true,
            features: {
              cellTooltip: {
                ...cellAvatarTooltipConfig,
                cls: "swivu-modern-tooltip"
              },
              cellMenu: false,
              stripe: false,
              sort: false
            },
            cls: "swivu-notify-grid swivu-hidden-headers h-full",
            columns: [
              {
                id: "type",
                field: "type",
                editor: false,
                autoHeight: true,
                renderer: "up.notificationTypeColumnRenderer",
                width: "30rem"
              },
              {
                id: "notify-tab-users-column",
                type: WIDGET_KEYS.notifyTabUserColumn,
              }
            ],
            listeners: {
              cellClick: "up.onGridCellClick"
            }
          }
        },
        swivuAssistance: SWIVU_ASSIST_CONFIG.ownership
      }
    }

    get SWIVU_NOTIFY_REMOVE_BUTTON_CLS() {
      return "swivu-notify-remove-button";
    }

    get isSyncEnabled() {
      return userPreferences.getSetting(PERSISTENCE_STATE_KEYS.syncUserNotifyPreferenceChanges, false) === "true";
    }

    loadRecord(record, context, otherParams) {
      if(!otherParams.isFirstShow) return;
      super.loadRecord(record, context);
      this.widgetMap.notifyGrid.columns.getById("notify-tab-users-column").onLoad(context.project, { swivuTab: this });
      this.updateGridData();
      this.handleStoreListeners();
      this.handleElementListeners();
      if (this.widgetMap.syncChangesAcrossTypesCheckbox) {
        this.widgetMap.syncChangesAcrossTypesCheckbox.checked = this.isSyncEnabled;
      }
    }

    updateGridData() {
      const { notifyGrid } = this.widgetMap;
      notifyGrid.data = this.getGridData();
    }

    getGridData() {
      const data = [];
      const currentTabType = this.type;
      for (const key in NOTIFY_TYPE_RECORD_KEYS) {
        const recordKey = NOTIFY_TYPE_RECORD_KEYS[key];
        if (EXCLUDE_NOTIFY_TYPE_BY_TAB[currentTabType]?.includes(recordKey)) continue;
        data.push({
          type: recordKey,
          values: this.getBoardUserValueByNotifyType(recordKey)
        })
      }
      return data;
    }

    getBoardUserValueByNotifyType(type) {
      const store = this.project.getCrudStore(STORE_BY_NOTIFY_TYPE_KEY_MAP[type]);
      if (store.id === STORE_KEYS.resourceStore) {
        return this.record[type];
      } else {
        const boardUserIds = store.query((record) => record[this.recordType] === this.record.id && record[type] === true, true).map(notifyBoardUserRecord => notifyBoardUserRecord.boardUserId);
        return boardUserIds;
      }
    }

    notificationTypeColumnRenderer({ record }) {
      const { icon, title, description } = NOTIFY_TYPE_COLUMN_CONTENT[record.type];
      return {
        tag: "div",
        class: "flex items-start gap-swivu-horizontal p-2",
        children: [
          {
            tag: "i",
            class: `${icon} !text-primary text-swivu-icon flex-shrink-0 pt-1 mr-[6px] dark:!text-[#e6e6e6]`
          },
          {
            tag: "div",
            class: "flex flex-col gap-1 min-w-0",
            children: [
              {
                tag: "div",
                class: "font-medium text-swivu-base dark:text-[#e6e6e6] text-black leading-none",
                text: title
              },
              {
                tag: "div",
                class: "text-swivu-sm dark:text-[#d1d5db] text-gray-500 leading-tight",
                text: description(this.entityNamePlaceholder)
              }
            ]
          }
        ]
      }
    }

    handleStoreListeners() {
      const { project } = this;
      const listeners = [STORE_KEYS.notifyeeBoardUsers, STORE_KEYS.resourceStore].map(storeId => {
        return project.getCrudStore(storeId).on("change", this.updateGridData, this);
      });
      this.addListenerToCleanupStore(...listeners);
    }

    handleElementListeners() {
      const { notifyGrid } = this.widgetMap;
      const listener = EventHelper.on({
        element: notifyGrid.element,
        click: ({ target }) => {
          if (!target.classList.contains(this.SWIVU_NOTIFY_REMOVE_BUTTON_CLS)) return;
          const { gridRecordId, boardUserId } = target.dataset;
          const { type, values: boardUserIds } = notifyGrid.store.getById(gridRecordId);
          const updatedBoardUserId = Number(boardUserId);

          this.handleSwivuRecordUpdate({
            notifyType: type,
            newBoardUserIds: boardUserIds.filter(id => id !== updatedBoardUserId),
            updatedBoardUserId,
            action: "remove"
          });
        }
      })
      this.addListenerToCleanupStore(listener);
    }

    async handleSwivuRecordUpdate(e) {
      if (!this.isSyncEnabled) {
        this.syncSwivuRecordUpdate(e);
        return;
      }

      const promise = this.project.commitAsync();

      for (const notifyType of Object.values(NOTIFY_TYPE_RECORD_KEYS)) {
        if (EXCLUDE_NOTIFY_TYPE_BY_TAB[this.type]?.includes(notifyType)) continue; // Skip the notify types that are excluded in this tab
        const boardUsers = this.getBoardUserValueByNotifyType(notifyType);
        const { action, updatedBoardUserId } = e;

        /* here we have to manully check the updated board user ids for each notify type, Based on user action we either remove the updated one from the board users or add that one to the board users */
        const newBoardUserIds = action === "remove" ? boardUsers.filter(id => id !== updatedBoardUserId) : [...boardUsers, updatedBoardUserId];


        /* The swivu sync function willl decide when to create a new notify board user record and when to simply just update the existing records */
        this.syncSwivuRecordUpdate({
          ...e,
          newBoardUserIds,
          notifyType
        });
      }

      await promise;
    }

    syncSwivuRecordUpdate(e) {
      const { notifyType, newBoardUserIds, updatedBoardUserId, action } = e;
      const store = this.project.getCrudStore(STORE_BY_NOTIFY_TYPE_KEY_MAP[notifyType]);
      if (store.id === STORE_KEYS.resourceStore) {
        this.record[notifyType] = newBoardUserIds;
      } else {
        const notifyBoardUserRecord = store.find(record => record.boardUserId === updatedBoardUserId && record[this.recordType] === this.record.id);
        if (notifyBoardUserRecord) {
          notifyBoardUserRecord[notifyType] = action === "add";
        } else if (action === "add") {
          const newNotifyBoardUserRecord = store.createRecord({
            [notifyType]: true,
            boardUserId: updatedBoardUserId,
            [this.recordType]: this.record.id
          });
          store.add(newNotifyBoardUserRecord);
        }
      }
    }

    onSyncChangesAcrossTypesChange({ value, userAction }) {
      if (userAction) userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.syncUserNotifyPreferenceChanges, String(value));
    }

    addListenerToCleanupStore(...listeners) {
      if (!this.swivuListenerCleanupFnStore) this.swivuListenerCleanupFnStore = [];
      this.swivuListenerCleanupFnStore.push(...listeners);
    }

    onSwivuEditorHide() {
      super.onSwivuEditorHide();
      if (this.swivuListenerCleanupFnStore) {
        this.swivuListenerCleanupFnStore.forEach(destroy => destroy());
        this.swivuListenerCleanupFnStore = null;
      }
    }

    onGridCellClick({ record, column, event }) {
      if (event.target.classList.contains(this.SWIVU_NOTIFY_REMOVE_BUTTON_CLS)) return;
      if (column.id === "notify-tab-users-column") {
        this.editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
          entityRecord: record,
          event,
          column
        }, { by: "recordType", useExisting: true });
      }
    }
  }
)
